import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Samples and inferences.
 *
 * The arithmetic is proportional reasoning the student already has, so these
 * templates put the weight on the two things that are actually new: keeping the
 * *proportion* rather than the count when scaling up, and judging whether a
 * sample was allowed to speak for its population at all.
 *
 * The bias questions are multiple choice on purpose. There is no number to
 * check, and the reasoning — who could not have been picked? — is the answer.
 */
export const SAMPLING_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'sampling-scale-to-population',
    conceptId: 'sampling',
    description: 'Scale a proportion found in a random sample up to the whole population.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const sample = rng.pick(difficulty === 3 ? [20, 25, 40, 50, 80] : [10, 20, 25, 50]);
      const multiplier = rng.pick(difficulty === 3 ? [6, 8, 10, 12, 15, 20] : [4, 5, 6, 10]);
      const population = sample * multiplier;
      const found = rng.int(2, sample - 2);
      const answer = found * multiplier;
      const scenario = rng.pick([
        { population: 'students in a school', trait: 'walk to school' },
        { population: 'households in a town', trait: 'own a bicycle' },
        { population: 'trees in a wood', trait: 'are oaks' },
      ]);

      return numericExercise({
        prompt: `There are ${population} ${scenario.population}. In a random sample of ${sample}, ${found} ${scenario.trait}. Estimate how many of all ${population} ${scenario.trait}.`,
        value: answer,
        summary: `${found} out of ${sample} is the same proportion as ${answer} out of ${population}, because ${population} is ${multiplier} times ${sample}.`,
        hint: 'Keep the proportion, not the count. How many times bigger is the population than the sample?',
        steps: [
          { label: 'Find how many times the population exceeds the sample.', latex: `\\frac{${population}}{${sample}} = ${multiplier}` },
          { label: 'Scale the count found in the sample by the same amount.', latex: `${found} \\times ${multiplier} = ${answer}` },
        ],
        traps: [
          // The sample count reported as if it were the population count.
          trap(found, 'sample-count-not-scaled'),
          // Adding the difference in size rather than scaling by it.
          ...(found + (population - sample) === answer ? [] : [trap(found + (population - sample), 'proportion-additive')]),
        ],
      });
    },
  }),

  template({
    id: 'sampling-proportion-as-percent',
    conceptId: 'sampling',
    description: 'Report a sample result as a percentage of the population.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const sample = rng.pick([20, 25, 40, 50, 80, 200]);
      const pct = rng.pick([5, 10, 15, 20, 25, 30, 40, 60, 75]);
      const found = (sample * pct) / 100;
      const population = sample * rng.pick([4, 5, 10, 20]);

      return numericExercise({
        prompt: `In a random sample of ${sample} people, ${found} said they cycle to work. What percentage of the ${population} people in the whole town would you estimate cycle to work?`,
        unit: '%',
        value: pct,
        summary: `${found} out of ${sample} is ${pct}%, and a random sample is taken to have the same proportion as the population — so the estimate is ${pct}%, whatever the population size.`,
        hint: 'The population size does not enter this one at all. A percentage is already a proportion.',
        steps: [
          { label: 'Write the sample result as a fraction.', latex: `\\frac{${found}}{${sample}}` },
          { label: 'Turn it into a percentage.', latex: `${pct}\\%` },
        ],
        traps: [
          // The count given where a percentage was asked for.
          ...(found === pct ? [] : [trap(found, 'sample-count-not-scaled')]),
          // The proportion taken against the population instead of the sample.
          ...(roundTo((found / population) * 100, 4) === pct ? [] : [trap(roundTo((found / population) * 100, 4), 'percent-change-wrong-base')]),
        ],
      });
    },
  }),

  template({
    id: 'sampling-capture-recapture',
    conceptId: 'sampling',
    description: 'Estimate a population size from a tagged sample recaptured later.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const tagged = rng.pick(difficulty === 5 ? [40, 50, 60, 75, 80, 120] : [20, 30, 40, 50]);
      const recaught = rng.int(3, 12);
      const multiplier = rng.pick(difficulty === 5 ? [6, 8, 10, 12, 15] : [4, 5, 6, 8]);
      const secondCatch = recaught * multiplier;
      const answer = tagged * multiplier;
      const creature = rng.pick([
        { noun: 'fish', place: 'a lake' },
        { noun: 'rabbits', place: 'a field' },
        { noun: 'beetles', place: 'a garden' },
      ]);

      return numericExercise({
        prompt: `${tagged} ${creature.noun} in ${creature.place} are caught, tagged and released. Later, ${secondCatch} are caught and ${recaught} of them carry tags. Estimate how many ${creature.noun} there are altogether.`,
        unit: creature.noun,
        value: answer,
        summary: `${recaught} of ${secondCatch} carried tags — one in ${multiplier}. If the same proportion of the whole population is tagged, the ${tagged} tagged ones are one in ${multiplier} of everything, giving ${answer}.`,
        hint: 'What fraction of the second catch was tagged? Assume the whole population is tagged in that same proportion.',
        steps: [
          { label: 'Find the proportion tagged in the second catch.', latex: `\\frac{${recaught}}{${secondCatch}} = \\frac{1}{${multiplier}}` },
          { label: 'The tagged fish are that same proportion of the population.', latex: `\\frac{${tagged}}{N} = \\frac{1}{${multiplier}}` },
          { label: 'Solve for the population.', latex: `N = ${tagged} \\times ${multiplier} = ${answer}` },
        ],
        traps: [
          // The two catches added, as though every fish had been counted once.
          ...(tagged + secondCatch === answer ? [] : [trap(tagged + secondCatch, 'sample-count-not-scaled')]),
          // The proportion inverted, giving a population smaller than the tags.
          ...(roundTo(tagged / multiplier, 4) === answer ? [] : [trap(roundTo(tagged / multiplier, 4), 'divide-wrong-direction')]),
        ],
      });
    },
  }),

  template({
    id: 'sampling-identify-bias',
    conceptId: 'sampling',
    description: 'Choose the sample that may be trusted to represent the population.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const size = rng.pick([30, 40, 50, 60, 80, 100]);
      const scenario = rng.pick([
        {
          question: 'A school wants to know what proportion of its students walk to school.',
          fair: `${size} students chosen at random from the full register`,
          biased: [
            `the first ${size} students to arrive at the gate`,
            `${size} students taken from the cycling club`,
            `${size} students who volunteered to be asked`,
          ],
        },
        {
          question: 'A town wants to know what proportion of its residents use the library.',
          fair: `${size} residents chosen at random from the electoral roll`,
          biased: [
            `${size} people interviewed inside the library`,
            `${size} people who replied to a notice on the library website`,
            `${size} students at the town's sixth-form college`,
          ],
        },
        {
          question: 'A council wants to know what proportion of households recycle.',
          fair: `${size} households chosen at random from the council tax list`,
          biased: [
            `${size} households on the street nearest the recycling centre`,
            `${size} households who returned a recycling survey by post`,
            `${size} households visited on a weekday morning`,
          ],
        },
      ]);
      const wrong = rng.shuffle(scenario.biased).slice(0, 3);

      const { choices, correctId } = options(
        [
          { label: scenario.fair, correct: true },
          { label: wrong[0] as string, misconceptionId: 'biased-sample-trusted' },
          { label: wrong[1] as string, misconceptionId: 'biased-sample-trusted' },
          { label: wrong[2] as string, misconceptionId: 'biased-sample-trusted' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${scenario.question} Which sample can be trusted to represent the whole population?`,
        choices,
        correctId,
        summary: 'Only a sample drawn at random gives every member of the population a chance of being picked. The others each shut somebody out before a single question is asked.',
        hint: 'Ask who could not possibly have ended up in each sample.',
        steps: [
          { label: 'A trustworthy sample is chosen at random from the whole population.', latex: '\\text{every member has a chance}' },
          { label: 'Anything that favours one group answers a narrower question than the one asked.', latex: '\\text{bias}' },
        ],
      });
    },
  }),

  template({
    id: 'sampling-bigger-sample',
    conceptId: 'sampling',
    description: 'Decide what a larger sample does, and what it cannot fix.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const small = rng.pick([10, 20, 25, 40]);
      const large = small * rng.pick([5, 10, 20]);

      const { choices, correctId } = options(
        [
          {
            label: `The random sample of ${large}, because a larger random sample varies less from one survey to the next.`,
            correct: true,
          },
          {
            label: `The club sample of ${large}, because it is just as large and easier to collect.`,
            misconceptionId: 'biased-sample-trusted',
          },
          {
            label: `The club sample of ${large}, because size is what makes a sample reliable.`,
            misconceptionId: 'biased-sample-trusted',
          },
          {
            label: `Neither — a sample of ${small} is already enough, so the extra people tell you nothing.`,
            misconceptionId: 'sample-count-not-scaled',
          },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A survey asks how often people cycle. One option is a random sample of ${large} residents; the other is ${large} members of the local cycling club. A pilot study used a random sample of ${small}. Which sample gives the best estimate for the town, and why?`,
        choices,
        correctId,
        summary: `Randomness is what makes a sample representative, and size is what makes it steady. A club sample of ${large} answers a question about cyclists very precisely, and about the town not at all.`,
        hint: 'One of these two problems is fixed by asking more people. The other is not fixed by asking more people at all.',
        steps: [
          { label: 'Random selection removes bias.', latex: '\\text{who is asked}' },
          { label: 'A larger sample reduces how much the answer moves between surveys.', latex: '\\text{how many are asked}' },
        ],
      });
    },
  }),
];
